import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CARDIO_BY_ID, estimateCalories } from '../data/cardio';
import { getExercise } from '../data/exercises';
import { fetchHealth, mapActivityToCardioType, type HealthDay } from '../lib/health';
import { blockInfo } from '../engine/blocks';
import { generateWorkout } from '../engine/generator';
import { suggestLoad } from '../engine/progression';
import type { SessionTemplate } from '../data/splits';
import { addDays as addDaysIso, todayIso } from '../lib/dates';
import { loadState, saveState } from './storage';
import type {
  AppState,
  BodyMetric,
  CardioSession,
  HealthSettings,
  LoggedExercise,
  Profile,
  SetLog,
  Settings,
  WorkoutSession,
} from './types';

function uid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

interface AppStore {
  state: AppState;
  setProfile: (patch: Partial<Profile>) => void;
  setSettings: (patch: Partial<Settings>) => void;
  toggleEquipment: (id: string) => void;
  setEquipment: (ids: string[]) => void;

  startWorkout: (template: SessionTemplate, dateIso?: string) => void;
  cancelWorkout: () => void;
  finishWorkout: (notes?: string) => void;
  updateSet: (exerciseIndex: number, setIndex: number, patch: Partial<SetLog>) => void;
  addSet: (exerciseIndex: number) => void;
  removeSet: (exerciseIndex: number) => void;
  toggleWarmup: (exerciseIndex: number, warmupIndex: number) => void;
  swapExercise: (exerciseIndex: number, newExerciseId: string) => void;
  skipExercise: (exerciseIndex: number) => void;
  setExerciseNote: (exerciseIndex: number, note: string) => void;

  addCardio: (entry: Omit<CardioSession, 'id'>) => void;
  deleteCardio: (id: string) => void;

  addMetric: (entry: Omit<BodyMetric, 'id'>) => void;
  deleteMetric: (id: string) => void;

  syncHealth: () => Promise<{ days: number; cardioAdded: number }>;
  setHealth: (patch: Partial<HealthSettings>) => void;

  toggleFavorite: (exerciseId: string) => void;
  toggleExcluded: (exerciseId: string) => void;

  replaceState: (next: AppState) => void;
  deleteWorkout: (id: string) => void;
}

const Ctx = createContext<AppStore | null>(null);

export function AppStoreProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(() => loadState());

  useEffect(() => {
    saveState(state);
  }, [state]);

  const patchActive = useCallback(
    (fn: (w: WorkoutSession) => WorkoutSession) => {
      setState((s) => (s.activeWorkout ? { ...s, activeWorkout: fn(s.activeWorkout) } : s));
    },
    [],
  );

  const patchExercise = useCallback(
    (index: number, fn: (e: LoggedExercise) => LoggedExercise) => {
      patchActive((w) => ({
        ...w,
        exercises: w.exercises.map((e, i) => (i === index ? fn(e) : e)),
      }));
    },
    [patchActive],
  );

  const store = useMemo<AppStore>(
    () => ({
      state,

      setProfile: (patch) =>
        setState((s) => ({ ...s, profile: { ...s.profile, ...patch } })),

      setSettings: (patch) =>
        setState((s) => ({ ...s, settings: { ...s.settings, ...patch } })),

      toggleEquipment: (id) =>
        setState((s) => {
          const has = s.settings.equipment.includes(id);
          return {
            ...s,
            settings: {
              ...s.settings,
              equipment: has
                ? s.settings.equipment.filter((e) => e !== id)
                : [...s.settings.equipment, id],
            },
          };
        }),

      setEquipment: (ids) =>
        setState((s) => ({ ...s, settings: { ...s.settings, equipment: ids } })),

      startWorkout: (template, dateIso = todayIso()) =>
        setState((s) => {
          const block = blockInfo(s.settings, dateIso);
          const generated = generateWorkout({
            template,
            equipment: new Set(s.settings.equipment),
            workouts: s.workouts,
            prefs: s.exercisePrefs,
            block,
            goal: s.profile.goal,
            useSupersets: s.settings.useSupersets,
          });

          return {
            ...s,
            activeWorkout: {
              id: uid(),
              date: dateIso,
              startedAt: new Date().toISOString(),
              splitId: block.splitId,
              templateId: template.id,
              templateName: template.name,
              blockNumber: block.blockNumber,
              weekInBlock: block.weekInBlock,
              isDeload: block.isDeload,
              exercises: generated.exercises,
              bodyweightKg: s.profile.bodyweightKg,
            },
          };
        }),

      cancelWorkout: () => setState((s) => ({ ...s, activeWorkout: null })),

      finishWorkout: (notes) =>
        setState((s) => {
          if (!s.activeWorkout) return s;
          const finished: WorkoutSession = {
            ...s.activeWorkout,
            finishedAt: new Date().toISOString(),
            notes,
            // Drop exercises you never touched so they don't pollute your
            // history or your weekly volume numbers.
            exercises: s.activeWorkout.exercises
              .filter((e) => !e.skipped && e.sets.some((set) => set.done))
              .map((e) => ({ ...e, sets: e.sets.filter((set) => set.done) })),
          };
          if (finished.exercises.length === 0) {
            return { ...s, activeWorkout: null };
          }
          return { ...s, workouts: [...s.workouts, finished], activeWorkout: null };
        }),

      updateSet: (exerciseIndex, setIndex, patch) =>
        patchExercise(exerciseIndex, (e) => ({
          ...e,
          sets: e.sets.map((set, i) => (i === setIndex ? { ...set, ...patch } : set)),
        })),

      addSet: (exerciseIndex) =>
        patchExercise(exerciseIndex, (e) => {
          const last = e.sets[e.sets.length - 1];
          return {
            ...e,
            sets: [
              ...e.sets,
              { weightKg: last?.weightKg ?? e.suggestionKg ?? 0, reps: 0, done: false },
            ],
          };
        }),

      removeSet: (exerciseIndex) =>
        patchExercise(exerciseIndex, (e) => ({
          ...e,
          sets: e.sets.length > 1 ? e.sets.slice(0, -1) : e.sets,
        })),

      toggleWarmup: (exerciseIndex, warmupIndex) =>
        patchExercise(exerciseIndex, (e) => {
          // The ramp length changes with the working weight, so grow the array
          // on demand rather than assuming a fixed size.
          const done = [...(e.warmupsDone ?? [])];
          while (done.length <= warmupIndex) done.push(false);
          done[warmupIndex] = !done[warmupIndex];
          return { ...e, warmupsDone: done };
        }),

      swapExercise: (exerciseIndex, newExerciseId) =>
        setState((s) => {
          if (!s.activeWorkout) return s;
          const next = getExercise(newExerciseId);
          if (!next) return s;

          const block = blockInfo(s.settings, s.activeWorkout.date);
          const suggestion = suggestLoad(next, s.workouts, block.loadMultiplier);

          return {
            ...s,
            activeWorkout: {
              ...s.activeWorkout,
              exercises: s.activeWorkout.exercises.map((e, i) => {
                if (i !== exerciseIndex) return e;
                const sets = e.sets.length;
                return {
                  ...e,
                  swappedFrom: e.swappedFrom ?? e.exerciseId,
                  exerciseId: newExerciseId,
                  targetReps: next.repRange,
                  suggestionKg: suggestion.weightKg,
                  suggestionReason: suggestion.reason,
                  skipped: false,
                  sets: Array.from({ length: sets }, () => ({
                    weightKg: suggestion.weightKg ?? 0,
                    reps: 0,
                    done: false,
                  })),
                };
              }),
            },
          };
        }),

      skipExercise: (exerciseIndex) =>
        patchExercise(exerciseIndex, (e) => ({ ...e, skipped: !e.skipped })),

      setExerciseNote: (exerciseIndex, note) =>
        patchExercise(exerciseIndex, (e) => ({ ...e, note })),

      addCardio: (entry) =>
        setState((s) => ({ ...s, cardio: [...s.cardio, { ...entry, id: uid() }] })),

      deleteCardio: (id) =>
        setState((s) => ({ ...s, cardio: s.cardio.filter((c) => c.id !== id) })),

      addMetric: (entry) =>
        setState((s) => {
          const metric: BodyMetric = { ...entry, id: uid() };
          // Logging today's weight should also update the profile, otherwise
          // every calorie estimate keeps using a stale number.
          const profile =
            metric.weightKg && metric.date === todayIso()
              ? { ...s.profile, bodyweightKg: metric.weightKg }
              : s.profile;
          return { ...s, profile, metrics: [...s.metrics, metric] };
        }),

      deleteMetric: (id) =>
        setState((s) => ({ ...s, metrics: s.metrics.filter((m) => m.id !== id) })),

      setHealth: (patch) =>
        setState((s) => ({
          ...s,
          settings: { ...s.settings, health: { ...s.settings.health, ...patch } },
        })),

      syncHealth: async () => {
        const { health } = state.settings;
        const payload = await fetchHealth(
          { endpoint: health.endpoint, token: health.token },
          // Re-fetch a fortnight each time so late-arriving samples and
          // corrections land, without dragging down the whole history.
          addDaysIso(todayIso(), -14),
        );

        let cardioAdded = 0;

        setState((s) => {
          // Merge by date, newest data winning, so a re-sync corrects a day
          // rather than duplicating it.
          const byDate = new Map<string, HealthDay>();
          for (const day of s.healthDays) byDate.set(day.date, day);
          for (const day of payload.days) {
            byDate.set(day.date, { ...byDate.get(day.date), ...day });
          }

          const imported = new Set(s.importedWorkoutIds);
          const newCardio: CardioSession[] = [];

          if (s.settings.health.autoImportWorkouts) {
            for (const workout of payload.workouts) {
              if (imported.has(workout.id)) continue;

              const typeId = mapActivityToCardioType(workout.activity, workout.source);
              // Unrecognised or deliberately ignored (strength work you already
              // log set by set) — mark it seen so we don't reconsider it.
              if (!typeId) {
                imported.add(workout.id);
                continue;
              }

              const minutes = Math.round(workout.minutes ?? 0);
              if (minutes <= 0) {
                imported.add(workout.id);
                continue;
              }

              const type = CARDIO_BY_ID[typeId];
              const measured = typeof workout.energyKcal === 'number' && workout.energyKcal > 0;

              newCardio.push({
                id: uid(),
                date: workout.date,
                typeId,
                minutes,
                distanceKm: workout.distanceKm ?? undefined,
                avgHr: workout.avgHr ?? undefined,
                calories: measured
                  ? Math.round(workout.energyKcal!)
                  : estimateCalories(type?.met ?? 6, s.profile.bodyweightKg, minutes),
                // A figure your watch actually measured beats our MET estimate.
                caloriesEstimated: !measured,
                notes: `From Apple Health${workout.source ? ` · ${workout.source}` : ''}`,
              });
              imported.add(workout.id);
            }
          }

          cardioAdded = newCardio.length;

          return {
            ...s,
            healthDays: [...byDate.values()].sort((a, b) => (a.date < b.date ? 1 : -1)),
            importedWorkoutIds: [...imported],
            cardio: [...s.cardio, ...newCardio],
            settings: {
              ...s.settings,
              health: { ...s.settings.health, lastSyncedAt: new Date().toISOString() },
            },
          };
        });

        return { days: payload.days.length, cardioAdded };
      },

      toggleFavorite: (exerciseId) =>
        setState((s) => {
          const has = s.exercisePrefs.favorites.includes(exerciseId);
          return {
            ...s,
            exercisePrefs: {
              ...s.exercisePrefs,
              favorites: has
                ? s.exercisePrefs.favorites.filter((id) => id !== exerciseId)
                : [...s.exercisePrefs.favorites, exerciseId],
            },
          };
        }),

      toggleExcluded: (exerciseId) =>
        setState((s) => {
          const has = s.exercisePrefs.excluded.includes(exerciseId);
          return {
            ...s,
            exercisePrefs: {
              ...s.exercisePrefs,
              excluded: has
                ? s.exercisePrefs.excluded.filter((id) => id !== exerciseId)
                : [...s.exercisePrefs.excluded, exerciseId],
            },
          };
        }),

      replaceState: (next) => setState(next),

      deleteWorkout: (id) =>
        setState((s) => ({ ...s, workouts: s.workouts.filter((w) => w.id !== id) })),
    }),
    [state, patchExercise, patchActive],
  );

  return <Ctx.Provider value={store}>{children}</Ctx.Provider>;
}

export function useApp(): AppStore {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useApp must be used inside AppStoreProvider');
  return ctx;
}
